import { Link } from "react-router-dom";
import { CheckCircle2, Factory, ShieldCheck, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import SectionHeader from "@/components/common/SectionHeader";
import {
  b2bStrengthPoints,
  clientLogoItems,
  corporateBrandingUseCases,
  manufacturingSteps,
  nakshatraBrand,
  productCategories,
  services,
  trustIndicators,
} from "@/data/siteContent";

import corporate from "@/assets/images/bobb.jpeg";

const iconMap = [ShieldCheck, Factory, Truck, CheckCircle2];

const HomePage = () => {
  return (
    <div data-testid="home-page-root">
      <section className="industrial-hero border-b border-slate-200" data-testid="home-hero-section">
        <div className="mx-auto grid w-full max-w-7xl gap-12 px-4 py-14 sm:px-6 md:py-20 lg:grid-cols-2 lg:px-8">
          <div className="space-y-7 self-center" data-testid="home-hero-content">
            <p
              className="inline-flex rounded-full border border-slate-300 bg-white px-4 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600"
              data-testid="home-hero-eyebrow"
            >
              Parent Manufacturing Company
            </p>
            <h1 className="text-4xl font-semibold text-slate-900 sm:text-5xl lg:text-6xl" data-testid="home-hero-title">
              Umbrella Manufacturer & Corporate Branding Specialist
            </h1>
            <p className="text-xl font-semibold text-slate-800" data-testid="home-hero-supporting-line">
              Makers of Nakshatra Brand Umbrellas
            </p>
            <p className="text-base leading-relaxed text-slate-700 md:text-lg" data-testid="home-hero-description">
              Keshavlal & Company delivers reliable umbrella manufacturing and custom branded umbrella
              supply for wholesalers, distributors, and corporate campaigns across India.
            </p>

            <ul className="space-y-2" data-testid="home-hero-b2b-points-list">
              {b2bStrengthPoints.map((point) => (
                <li
                  key={point}
                  className="flex items-start gap-2 text-sm font-medium text-slate-700"
                  data-testid={`home-hero-b2b-point-${point.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-orange-500" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3" data-testid="home-hero-cta-group">
              <Button
                asChild
                className="rounded-full bg-orange-500 px-6 py-6 text-sm font-semibold text-white hover:bg-orange-600"
                data-testid="home-hero-request-quote-button"
              >
                <Link to="/contact" data-testid="home-hero-request-quote-link">
                  Request Corporate Quote
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-full border-slate-300 px-6 py-6 text-sm font-semibold"
                data-testid="home-hero-view-nakshatra-button"
              >
                <Link to="/nakshatra-umbrellas" data-testid="home-hero-view-nakshatra-link">
                  View Nakshatra Umbrellas
                </Link>
              </Button>
            </div>
          </div>

          <div className="relative" data-testid="home-hero-image-wrapper">
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_keshal-bulk-supply/artifacts/165lfor8_Logo1.jpg"
                alt="Umbrella manufacturing visual"
                className="safe-media-image"
                data-testid="home-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-950" data-testid="home-legacy-highlight-section">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div
            className="rounded-2xl border border-slate-700 bg-slate-900 px-6 py-8 text-center"
            data-testid="home-legacy-highlight-card"
          >
            <p
              className="text-xs font-semibold uppercase tracking-[0.14em] text-orange-400"
              data-testid="home-legacy-highlight-eyebrow"
            >
              Legacy Trust Messaging
            </p>
            <h2 className="mt-3 text-3xl font-semibold text-white sm:text-4xl" data-testid="home-legacy-highlight-title">
              4 Generations of Umbrella Manufacturing Experience
            </h2>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white" data-testid="home-trust-section">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <SectionHeader
            title="Manufacturing Strength You Can Trust"
            description="Built on legacy, scale, and consistency for bulk corporate demand."
            testId="home-trust-header"
          />
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-trust-grid">
            {trustIndicators.map((item, index) => {
              const Icon = iconMap[index % iconMap.length];
              return (
                <Card key={item.title} className="rounded-2xl border-slate-200" data-testid={`home-trust-card-${index}`}>
                  <CardContent className="space-y-3 p-5">
                    <Icon className="h-6 w-6 text-slate-800" />
                    <h3 className="text-lg font-semibold text-slate-900" data-testid={`home-trust-title-${index}`}>
                      {item.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-slate-600" data-testid={`home-trust-detail-${index}`}>
                      {item.detail}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-50" data-testid="home-services-section">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <SectionHeader
            title="Core Services"
            description="From private label umbrella supply to custom logo umbrella campaigns."
            testId="home-services-header"
          />
          <div className="mt-8 grid gap-5 md:grid-cols-3" data-testid="home-services-grid">
            {services.map((service, index) => (
              <Card key={service.title} className="rounded-2xl border-slate-200 bg-white" data-testid={`home-services-card-${index}`}>
                <CardContent className="space-y-3 p-6">
                  <h3 className="text-xl font-semibold text-slate-900" data-testid={`home-services-title-${index}`}>
                    {service.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-slate-600" data-testid={`home-services-description-${index}`}>
                    {service.detail}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white" data-testid="home-nakshatra-brand-section">
        <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="home-nakshatra-logo-block">
            <img
              src={nakshatraBrand.logo}
              alt="Nakshatra Umbrellas brand logo"
              className="safe-media-image mx-auto max-w-md"
              data-testid="home-nakshatra-brand-logo"
            />
          </div>

          <div className="space-y-4 self-center" data-testid="home-nakshatra-content-block">
            <h2 className="text-3xl font-semibold text-slate-900 sm:text-4xl" data-testid="home-nakshatra-title">
              Our In-House Brand – Nakshatra Umbrellas
            </h2>
            <p className="text-base leading-relaxed text-slate-700 md:text-lg" data-testid="home-nakshatra-description">
              {nakshatraBrand.description}
            </p>
            <Button
              asChild
              className="rounded-full bg-orange-500 px-6 py-5 text-sm font-semibold text-white hover:bg-orange-600"
              data-testid="home-nakshatra-view-button"
            >
              <Link to="/nakshatra-umbrellas" data-testid="home-nakshatra-view-link">
                View Nakshatra Umbrellas
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white" data-testid="home-product-categories-section">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <SectionHeader
            title="Product Categories"
            description="Wide-range umbrella and outdoor branding products for business requirements."
            testId="home-product-header"
          />
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-product-grid">
            {productCategories.map((product) => (
              <article
                key={product.slug}
                className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white"
                data-testid={`home-product-card-${product.slug}`}
              >
                <img
                  src={product.image}
                  alt={product.title}
                  className="safe-media-card"
                  data-testid={`home-product-image-${product.slug}`}
                />
                <div className="absolute left-3 top-3 flex gap-2" data-testid={`home-product-tags-${product.slug}`}>
                  <Badge className="bg-slate-900 text-white" data-testid={`home-product-tag-nakshatra-${product.slug}`}>
                    Nakshatra Range
                  </Badge>
                  <Badge className="bg-orange-500 text-white" data-testid={`home-product-tag-bulk-${product.slug}`}>
                    Bulk Packaging
                  </Badge>
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-semibold text-slate-900" data-testid={`home-product-title-${product.slug}`}>
                    {product.title}
                  </h3>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500" data-testid={`home-product-packaging-label-${product.slug}`}>
                    Dispatch-ready branding tags and packaging visuals
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-50" data-testid="home-corporate-branding-section">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="space-y-4" data-testid="home-corporate-branding-content">
            <SectionHeader
              title="Corporate Branding Umbrellas"
              description="Custom logo printed umbrellas for corporate promotions, dealership campaigns, and events."
              testId="home-corporate-branding-header"
            />
            <ul className="space-y-3" data-testid="home-corporate-branding-list">
              {corporateBrandingUseCases.map((useCase) => (
                <li key={useCase} className="flex gap-3 text-sm text-slate-700" data-testid={`home-corporate-branding-item-${useCase.toLowerCase().replace(/\s+/g, "-")}`}>
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-orange-500" />
                  <span>{useCase}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white" data-testid="home-corporate-branding-image-container">
            <img
              src= { corporate }
              alt="Custom printed umbrella campaign"
              className="safe-media-image"
              data-testid="home-corporate-branding-image"
            />
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white" data-testid="home-manufacturing-section">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <SectionHeader
            title="Factory-Based Manufacturing Process"
            description="From cutting to dispatch, each stage is quality controlled for durability and scale."
            testId="home-manufacturing-header"
          />
          <div className="mt-8 grid gap-4 md:grid-cols-3" data-testid="home-manufacturing-grid">
            {manufacturingSteps.map((step, index) => (
              <Card key={step.slug} className="rounded-2xl border-slate-200" data-testid={`home-manufacturing-card-${step.slug}`}>
                <CardContent className="space-y-2 p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-orange-600" data-testid={`home-manufacturing-step-number-${step.slug}`}>
                    Step {index + 1}
                  </p>
                  <h3 className="text-lg font-semibold text-slate-900" data-testid={`home-manufacturing-title-${step.slug}`}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-600" data-testid={`home-manufacturing-detail-${step.slug}`}>
                    {step.detail}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-50" data-testid="home-past-clients-section">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <SectionHeader
            title="Past Clients"
            description="Selected client logos from banking, automobile, retail, cement, and education sectors."
            testId="home-past-clients-header"
          />
          <div className="mt-8 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="home-past-clients-grid">
            {clientLogoItems.map((client, index) => (
              <div
                key={client.id}
                className="flex min-h-28 items-center justify-center rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                data-testid={`home-client-logo-card-${index}`}
              >
                <img
                  src={client.logoUrl}
                  alt={client.alt}
                  className="safe-media-logo"
                  data-testid={`home-client-logo-image-${index}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950" data-testid="home-final-cta-section">
        <div className="mx-auto w-full max-w-7xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-semibold text-white sm:text-4xl" data-testid="home-final-cta-title">
            Looking for Custom Logo Umbrellas? Contact Us Today.
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-base text-slate-300 md:text-lg" data-testid="home-final-cta-description">
            Share your quantity, branding requirements, and delivery timeline to receive a corporate proposal.
          </p>
          <Button
            asChild
            className="mt-6 rounded-full bg-orange-500 px-7 py-6 text-sm font-semibold text-white hover:bg-orange-600"
            data-testid="home-final-cta-button"
          >
            <Link to="/contact" data-testid="home-final-cta-link">
              Request Corporate Quote
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;